/**
 * Overlay Utilities - Helper functions for tap-out detection and overlay management
 * 
 * Used by Feature 2: Overlay Persistence & Unified Dismiss
 * Provides reusable functions to detect clicks outside overlays
 */

/**
 * Check if a click target is inside a specific element
 * @param clickTarget The target of the click event
 * @param element The element to check containment
 * @returns true if click is inside the element
 */
export function isClickInsideElement(
  clickTarget: EventTarget | null,
  element: HTMLElement | null
): boolean {
  if (!element || !(clickTarget instanceof HTMLElement)) return false
  return element.contains(clickTarget)
}

/**
 * Check if click should dismiss overlays (not on excluded elements)
 * @param clickTarget The target of the click event
 * @param excludeSelectors CSS selectors for elements that should NOT trigger dismiss
 * @returns true if click is outside all excluded elements
 */
export function shouldDismissOverlays(
  clickTarget: EventTarget | null,
  excludeSelectors: string[]
): boolean {
  if (!(clickTarget instanceof HTMLElement)) return false

  // Don't dismiss if clicking on excluded elements
  for (const selector of excludeSelectors) {
    if (clickTarget.closest(selector)) {
      return false // Click is on an excluded element
    }
  }

  return true // Click is outside all excluded elements
}

/**
 * Check if any element in a list contains the click target
 * @param clickTarget The target of the click event
 * @param elements Array of elements to check
 * @returns true if click is inside any of the elements
 */
export function isClickInsideAnyElement(
  clickTarget: EventTarget | null,
  elements: (HTMLElement | null)[]
): boolean {
  if (!(clickTarget instanceof HTMLElement)) return false

  for (const element of elements) {
    if (element && element.contains(clickTarget)) {
      return true // Click is inside this element
    }
  }

  return false // Click is outside all elements
}
